import { useEffect, useRef, useState, type PointerEvent, type WheelEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon.tsx";

export interface ViewerImage {
  url: string;
  caption: string;
}

const MAX = 6;

interface View {
  scale: number;
  /** Shift of the picture's centre from the stage's centre, in pixels. */
  x: number;
  y: number;
}

const START: View = { scale: 1, x: 0, y: 0 };

/**
 * A picture over the page, as large as the window allows. Zoom with the buttons,
 * the wheel, a double click or a pinch; drag to move; arrows for the next picture.
 */
export function ImageViewer({ images, start, onClose }: { images: ViewerImage[]; start: number; onClose: () => void }) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const [index, setIndex] = useState(start);
  const [view, setView] = useState<View>(START);
  const [moving, setMoving] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const image = images[index];

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (typeof d.showModal === "function") d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);

  /** Keeps the picture over the stage: no empty band beside a zoomed picture. */
  const clamp = (v: View): View => {
    const s = Math.min(MAX, Math.max(1, v.scale));
    const el = img.current;
    if (!el || s === 1) return { scale: s, x: 0, y: 0 };
    const sw = stage.current?.clientWidth ?? 0;
    const sh = stage.current?.clientHeight ?? 0;
    const maxX = Math.max(0, (el.clientWidth * s - sw) / 2);
    const maxY = Math.max(0, (el.clientHeight * s - sh) / 2);
    return { scale: s, x: Math.min(maxX, Math.max(-maxX, v.x)), y: Math.min(maxY, Math.max(-maxY, v.y)) };
  };

  /** Zooms keeping the point under (px, py) — page coordinates — where it is. */
  const zoomAt = (px: number, py: number, scale: number) =>
    setView((v) => {
      const r = stage.current?.getBoundingClientRect();
      if (!r) return v;
      const p = { x: px - (r.left + r.width / 2), y: py - (r.top + r.height / 2) };
      const s = Math.min(MAX, Math.max(1, scale));
      return clamp({ scale: s, x: p.x - ((p.x - v.x) * s) / v.scale, y: p.y - ((p.y - v.y) * s) / v.scale });
    });
  const zoomCentre = (k: number) => {
    const r = stage.current?.getBoundingClientRect();
    if (r) zoomAt(r.left + r.width / 2, r.top + r.height / 2, view.scale * k);
  };
  const go = (i: number) => {
    setIndex((i + images.length) % images.length);
    setView(START);
  };

  const onWheel = (e: WheelEvent<HTMLDivElement>) => {
    zoomAt(e.clientX, e.clientY, view.scale * Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.002)));
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setMoving(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const all = pointers.current;
    const prev = all.get(e.pointerId);
    if (!prev) return;
    const before = [...all.values()];
    all.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (all.size >= 2) {
      const [a, b] = before;
      const [c, d] = [...all.values()];
      const d0 = Math.hypot(a.x - b.x, a.y - b.y);
      const d1 = Math.hypot(c.x - d.x, c.y - d.y);
      if (d0 > 1) zoomAt((c.x + d.x) / 2, (c.y + d.y) / 2, (view.scale * d1) / d0);
      return;
    }
    setView((v) => clamp({ ...v, x: v.x + e.clientX - prev.x, y: v.y + e.clientY - prev.y }));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) setMoving(false);
  };

  return (
    <dialog
      ref={dialog}
      className="viewer"
      aria-label={image.caption}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" && images.length > 1) go(index + 1);
        else if (e.key === "ArrowLeft" && images.length > 1) go(index - 1);
        else if (e.key === "+" || e.key === "=") zoomCentre(1.5);
        else if (e.key === "-") zoomCentre(1 / 1.5);
        else if (e.key === "0") setView(START);
      }}
    >
      <div className="viewer-bar">
        <span className="viewer-caption">
          {image.caption}
          {images.length > 1 && <span className="muted"> · {index + 1}/{images.length}</span>}
        </span>
        <div className="viewer-tools">
          <button type="button" className="viewer-btn" aria-label={t("viewer.zoomOut")} disabled={view.scale <= 1} onClick={() => zoomCentre(1 / 1.5)}>
            <Icon name="minus" />
          </button>
          <span className="viewer-zoom mono" aria-live="polite">
            {Math.round(view.scale * 100)}%
          </span>
          <button type="button" className="viewer-btn" aria-label={t("viewer.zoomIn")} disabled={view.scale >= MAX} onClick={() => zoomCentre(1.5)}>
            <Icon name="plus" />
          </button>
          <button type="button" className="viewer-btn" aria-label={t("viewer.fit")} disabled={view.scale === 1} onClick={() => setView(START)}>
            <Icon name="fit" />
          </button>
          <a className="viewer-btn" href={image.url} target="_blank" rel="noreferrer" aria-label={t("viewer.original")} title={t("viewer.original")}>
            <Icon name="image" />
          </a>
          <button type="button" className="viewer-btn" aria-label={t("viewer.close")} onClick={onClose} autoFocus>
            <Icon name="close" />
          </button>
        </div>
      </div>
      <div
        ref={stage}
        className={`viewer-stage${view.scale > 1 ? " is-zoomed" : ""}${moving ? " is-moving" : ""}`}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => (view.scale > 1 ? setView(START) : zoomAt(e.clientX, e.clientY, 2.5))}
        onClick={(e) => {
          // A click on the dark backdrop around the picture closes it.
          if (e.target === e.currentTarget && view.scale === 1) onClose();
        }}
      >
        <img
          ref={img}
          src={image.url}
          alt={image.caption}
          draggable={false}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        />
      </div>
      {images.length > 1 && (
        <>
          <button type="button" className="viewer-nav viewer-prev" aria-label={t("viewer.previous")} onClick={() => go(index - 1)}>
            <Icon name="chevronLeft" size={22} />
          </button>
          <button type="button" className="viewer-nav viewer-next" aria-label={t("viewer.next")} onClick={() => go(index + 1)}>
            <Icon name="chevronRight" size={22} />
          </button>
        </>
      )}
    </dialog>
  );
}

/** Opens the viewer on a set of pictures: `open(i)` from a thumbnail, and the viewer to render. */
export function useImageViewer(images: ViewerImage[]) {
  const [at, setAt] = useState<number | null>(null);
  return {
    open: (i: number) => setAt(i),
    viewer: at !== null && images[at] ? <ImageViewer images={images} start={at} onClose={() => setAt(null)} /> : null,
  };
}
